// guest-keystore.ts — durable per-device keypair for anonymous ("guest") auth.
//
// A guest is an ed25519 keypair generated on the device. Its public key is the
// Bounded account address; it signs the auth challenge exactly like a wallet.
//
// Guest auth has one backend: WebCrypto Ed25519 + IndexedDB. The key is
// NON-EXTRACTABLE: an XSS can ask it to sign but can never read the private
// bytes. If WebCrypto or IndexedDB is unavailable, guest auth fails closed
// instead of silently creating an extractable/stored-secret credential.
//
// The public output is a base58 address + a base64
// detached signature, which is the format the session endpoint already accepts.
import bs58 from 'bs58';
import { getPlatform } from '../../platform';
const LEGACY_STORED_SECRET_KEY = 'bounded_guest_key_v1';
const IDB_NAME = 'bounded-guest';
const IDB_STORE = 'keys';
const IDB_KEY = 'ed25519';
const GUEST_KEY_REQUIREMENT = 'Guest auth requires non-extractable WebCrypto Ed25519 keys persisted in IndexedDB; ' +
    'stored-secret guest credentials are disabled.';
function toBase64(bytes) {
    let bin = '';
    for (let i = 0; i < bytes.length; i++)
        bin += String.fromCharCode(bytes[i]);
    // btoa exists in browsers + RN (Hermes) ; Buffer is the Node/SSR fallback.
    return typeof btoa !== 'undefined' ? btoa(bin) : Buffer.from(bytes).toString('base64');
}
// ----- WebCrypto (non-extractable) backend -----------------------------------
function subtle() {
    const c = globalThis === null || globalThis === void 0 ? void 0 : globalThis.crypto;
    return c && c.subtle ? c.subtle : null;
}
function idb() {
    return new Promise((resolve, reject) => {
        const req = globalThis.indexedDB.open(IDB_NAME, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}
function idbGet(db, key) {
    return new Promise((resolve, reject) => {
        const r = db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(key);
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error);
    });
}
// Atomic create-if-absent: the get and the conditional put happen inside ONE
// readwrite transaction, so a concurrent creation cannot land between the read and
// the write. Returns whatever ends up stored - the EXISTING key when another writer
// won the race, otherwise the candidate. It NEVER overwrites an existing key, so a
// racing tab's identity can no longer be stranded (DS4-0032).
//
// It settles on the TRANSACTION, not on the requests. A successful IndexedDB request
// is not a durable write: the transaction can still abort at commit (quota exhausted
// at flush, storage eviction), rolling the put back. Resolving from the request would
// report a key as saved that is not on disk, and the caller would then mint a session
// for an address this device cannot sign for - the same stranded identity from the
// other direction.
function idbGetOrPut(db, key, candidate) {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        const store = tx.objectStore(IDB_STORE);
        let stored;
        const getReq = store.get(key);
        getReq.onsuccess = () => {
            const existing = getReq.result;
            if (existing !== undefined && existing !== null) {
                stored = existing;
                return;
            }
            const putReq = store.put(candidate, key);
            putReq.onsuccess = () => { stored = candidate; };
        };
        // A committed transaction that produced neither an existing key nor a stored
        // candidate cannot happen, but resolving `undefined` would hand the caller a
        // key-less identity, so refuse instead of guessing.
        tx.oncomplete = () => stored === undefined || stored === null
            ? reject(new Error('IndexedDB guest-key write committed without a key'))
            : resolve(stored);
        tx.onabort = () => { var _a; return reject((_a = tx.error) !== null && _a !== void 0 ? _a : new Error('IndexedDB guest-key write was rolled back')); };
        tx.onerror = () => { var _a; return reject((_a = tx.error) !== null && _a !== void 0 ? _a : new Error('IndexedDB guest-key write failed')); };
    });
}
function guestKeyUnavailable(cause) {
    const detail = cause instanceof Error && cause.message ? ` Cause: ${cause.message}` : '';
    return new Error(`${GUEST_KEY_REQUIREMENT}${detail}`);
}
async function webCryptoKey(createIfMissing) {
    const s = subtle();
    if (!s)
        throw guestKeyUnavailable(new Error('WebCrypto subtle crypto is unavailable.'));
    if (typeof globalThis.indexedDB === 'undefined') {
        throw guestKeyUnavailable(new Error('IndexedDB is unavailable.'));
    }
    try {
        const db = await idb();
        let pair = (await idbGet(db, IDB_KEY));
        if (!pair) {
            if (!createIfMissing)
                return null;
            // Probe Ed25519 support by generating (throws on unsupported engines).
            const candidate = (await s.generateKey({ name: 'Ed25519' }, /* extractable */ false, [
                'sign',
                'verify',
            ]));
            // Persist atomically. If a concurrent creation already stored a key, keep
            // THEIRS and discard our candidate - never overwrite (DS4-0032). CryptoKey is
            // structured-cloneable; the private bytes are never exposed.
            pair = (await idbGetOrPut(db, IDB_KEY, candidate));
        }
        const raw = new Uint8Array(await s.exportKey('raw', pair.publicKey)); // public is exportable
        const address = bs58.encode(raw);
        return {
            address,
            nonExtractable: true,
            async sign(message) {
                const data = new TextEncoder().encode(message);
                const sig = new Uint8Array(await s.sign({ name: 'Ed25519' }, pair.privateKey, data));
                return toBase64(sig);
            },
        };
    }
    catch (error) {
        throw guestKeyUnavailable(error);
    }
}
let cached = null;
// Single-flight for creation: concurrent callers in ONE realm (a retry, two awaits,
// two components mounting at once) share a single creation rather than each racing
// to generate + persist. Cross-tab races are handled durably by idbGetOrPut; this
// closes the same-realm window where `cached` is not yet set (DS4-0032).
let creating = null;
/** Get (or create) this device's durable guest key. */
export async function getGuestKey() {
    if (cached)
        return cached;
    if (creating)
        return creating;
    creating = (async () => {
        const key = await webCryptoKey(true);
        if (!key)
            throw guestKeyUnavailable(new Error('Guest key creation returned no key.'));
        cached = key;
        return key;
    })();
    try {
        return await creating;
    }
    finally {
        creating = null;
    }
}
/** Read the existing durable guest key without ever minting a replacement. */
export async function getExistingGuestKey() {
    if (cached)
        return cached;
    cached = await webCryptoKey(false);
    return cached;
}
/** Forget the guest key on this device (a fresh signInAnonymously mints a new one). */
export async function clearGuestKey() {
    cached = null;
    // The legacy stored-secret cleanup stays best-effort: that key is not the durable
    // guest credential (it is a removed backend) and its absence is the normal case.
    try {
        getPlatform().storage.removeItem(LEGACY_STORED_SECRET_KEY);
    }
    catch (_a) { }
    // The IndexedDB delete is the DURABLE forget. A failure here must PROPAGATE
    // (DS2-0284): swallowing it - or resolving on the delete request's onerror -
    // reported a successful forget while the device key was still on disk, so a
    // caller (forgetGuest) believed the guest identity was wiped when it was not.
    if (typeof globalThis.indexedDB === 'undefined')
        return;
    const db = await idb();
    // Settle on the transaction for the same reason as idbGetOrPut: a delete request
    // that succeeded inside a transaction that later aborts leaves the key on disk,
    // and reporting THAT as a successful forget is exactly the DS2-0284 failure.
    await new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).delete(IDB_KEY);
        tx.oncomplete = () => resolve();
        tx.onabort = () => { var _a; return reject((_a = tx.error) !== null && _a !== void 0 ? _a : new Error('IndexedDB guest-key delete was rolled back')); };
        tx.onerror = () => { var _a; return reject((_a = tx.error) !== null && _a !== void 0 ? _a : new Error('IndexedDB guest-key delete failed')); };
    });
}
